import { type ZodType } from 'zod';
import {
  RussiaRequestAuditEntriesResponseSchema,
  RussiaRequestCarriersResponseSchema,
  RussiaRequestListResponseSchema,
  RussiaRequestManagerOptionsResponseSchema,
  RussiaRequestSchema,
  type RussiaRequest,
  type RussiaRequestAuditEntriesQuery,
  type RussiaRequestAuditEntriesResponse,
  type RussiaRequestCarriersResponse,
  type RussiaRequestListQuery,
  type RussiaRequestListResponse,
  type RussiaRequestManagerOptionsResponse,
} from '@magimp/shared';

import { request } from '../client.js';
import { serializeRussiaRequestFilters, withQuery } from './_query.js';

export function listRussiaRequests(
  filters: Partial<RussiaRequestListQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<RussiaRequestListResponse> {
  const path = withQuery('/russia-requests', serializeRussiaRequestFilters(filters));
  return request<RussiaRequestListResponse>(
    path,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RussiaRequestListResponseSchema as unknown as ZodType<RussiaRequestListResponse>,
  );
}

export function listRussiaRequestManagerOptions(
  opts: { signal?: AbortSignal } = {},
): Promise<RussiaRequestManagerOptionsResponse> {
  return request<RussiaRequestManagerOptionsResponse>(
    '/russia-requests/manager-options',
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RussiaRequestManagerOptionsResponseSchema as unknown as ZodType<RussiaRequestManagerOptionsResponse>,
  );
}

export function fetchRussiaRequest(
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<RussiaRequest> {
  return request<RussiaRequest>(
    `/russia-requests/${encodeURIComponent(id)}`,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
  );
}

function serializeAuditFilters(query: Partial<RussiaRequestAuditEntriesQuery>): string {
  const params = new URLSearchParams();
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return params.toString();
}

export function listRussiaRequestAuditEntries(
  id: string,
  query: Partial<RussiaRequestAuditEntriesQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<RussiaRequestAuditEntriesResponse> {
  const path = withQuery(
    `/russia-requests/${encodeURIComponent(id)}/audit-entries`,
    serializeAuditFilters(query),
  );
  return request<RussiaRequestAuditEntriesResponse>(
    path,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RussiaRequestAuditEntriesResponseSchema as unknown as ZodType<RussiaRequestAuditEntriesResponse>,
  );
}

export function listRussiaRequestCarriers(
  id: string,
  opts: { signal?: AbortSignal } = {},
): Promise<RussiaRequestCarriersResponse> {
  return request<RussiaRequestCarriersResponse>(
    `/russia-requests/${encodeURIComponent(id)}/carriers`,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RussiaRequestCarriersResponseSchema as unknown as ZodType<RussiaRequestCarriersResponse>,
  );
}
